/**
 * TownPanel — Main town UI overlay for Spellroads.
 *
 * Layout (desktop/tablet landscape):
 *   [Town Name + Race/Type badge]    [Close]
 *   ─────────────────────────────────────────
 *   [Service Sidebar] | [Service Content Area]
 *   ─────────────────────────────────────────
 *   [Leave Town]
 *
 * On mobile portrait the sidebar becomes a horizontal tab row
 * above the content area.
 *
 * Integrates with TownSystem when available; falls back to hardcoded
 * data for testing when TownSystem has not yet been loaded.
 *
 * @module TownPanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import WorldState from '../world/WorldState.js';

// ─── Style injection ──────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-town-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ══════════════════════════════════════════════════════════════
       TownPanel — Spellroads
       ══════════════════════════════════════════════════════════════ */

    #panel-town {
      position: fixed;
      inset: 0;
      z-index: 220;
      display: none;
      flex-direction: column;
      background: rgba(8, 6, 4, 0.97);
      pointer-events: all;
      overflow: hidden;
      font-family: 'Georgia', 'Times New Roman', serif;
      color: #e8d5b0;
    }

    #panel-town.visible { display: flex; }

    /* ── Header ──────────────────────────────────────────────────── */
    .town-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px);
      background: rgba(0, 0, 0, 0.55);
      border-bottom: 1px solid rgba(200, 160, 32, 0.25);
      flex-shrink: 0;
      gap: 12px;
    }

    .town-header-left {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .town-title {
      font-size: clamp(1rem, 2vw, 1.4rem);
      font-weight: 700;
      color: #c8a020;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .town-subtitle {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: clamp(0.7rem, 1.2vw, 0.85rem);
      color: #a09070;
    }

    .town-race-icon {
      font-size: clamp(0.85rem, 1.4vw, 1rem);
    }

    .town-type-badge {
      font-size: clamp(0.6rem, 0.9vw, 0.72rem);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 2px 7px;
      border-radius: 3px;
      background: rgba(200, 160, 32, 0.12);
      border: 1px solid rgba(200, 160, 32, 0.3);
      color: #c8a020;
    }

    .town-close {
      background: none;
      border: 1px solid rgba(200, 160, 32, 0.3);
      color: #a09070;
      cursor: pointer;
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      border-radius: 4px;
      font-size: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s, border-color 0.15s;
      flex-shrink: 0;
    }

    .town-close:hover,
    .town-close:focus-visible {
      color: #c8a020;
      border-color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    /* ── Body: sidebar + content ─────────────────────────────────── */
    .town-body {
      flex: 1;
      display: grid;
      grid-template-columns: 200px 1fr;
      overflow: hidden;
      min-height: 0;
    }

    /* ── Service sidebar ──────────────────────────────────────────── */
    .town-sidebar {
      display: flex;
      flex-direction: column;
      background: rgba(0, 0, 0, 0.3);
      border-right: 1px solid rgba(200, 160, 32, 0.15);
      overflow-y: auto;
      padding: 12px 8px;
      gap: 4px;
      flex-shrink: 0;
    }

    .town-sidebar-heading {
      font-size: 0.6rem;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      color: #706050;
      padding: 4px 8px 8px;
    }

    .town-service-btn {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      min-height: 44px;
      padding: 8px 12px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      color: #a09070;
      font-family: inherit;
      font-size: clamp(0.7rem, 1.1vw, 0.82rem);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      cursor: pointer;
      text-align: left;
      transition: background 0.12s, color 0.12s, border-color 0.12s;
    }

    .town-service-btn:hover,
    .town-service-btn:focus-visible {
      background: rgba(200, 160, 32, 0.08);
      color: #c8d0a0;
      border-color: rgba(200, 160, 32, 0.2);
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    .town-service-btn.active {
      background: rgba(200, 160, 32, 0.14);
      color: #c8a020;
      border-color: rgba(200, 160, 32, 0.4);
    }

    .town-service-icon {
      font-size: 1.1rem;
      flex-shrink: 0;
      width: 22px;
      text-align: center;
    }

    /* ── Content area ─────────────────────────────────────────────── */
    .town-content {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
    }

    .town-content-inner {
      flex: 1;
      overflow-y: auto;
      padding: clamp(12px, 2vw, 20px);
    }

    .town-content-title {
      font-size: clamp(0.75rem, 1.2vw, 0.9rem);
      font-weight: 700;
      color: #c8a020;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 14px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(200, 160, 32, 0.18);
    }

    /* ── Gold bar inside content ─────────────────────────────────── */
    .town-gold-bar {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      font-size: clamp(0.72rem, 1.1vw, 0.85rem);
      color: #d4b870;
      font-weight: 600;
      margin-bottom: 12px;
    }

    /* ── Shop items ───────────────────────────────────────────────── */
    .town-item-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .town-item-row {
      display: grid;
      grid-template-columns: auto 1fr auto auto;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(200, 160, 32, 0.1);
      border-radius: 4px;
      min-height: 52px;
      transition: background 0.12s, border-color 0.12s;
    }

    .town-item-row:hover {
      background: rgba(200, 160, 32, 0.06);
      border-color: rgba(200, 160, 32, 0.25);
    }

    .town-item-icon {
      font-size: 1.3rem;
      width: 28px;
      text-align: center;
      flex-shrink: 0;
    }

    .town-item-info { min-width: 0; }

    .town-item-name {
      font-size: clamp(0.68rem, 1.1vw, 0.8rem);
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .town-item-sub {
      font-size: 0.6rem;
      color: #706050;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-top: 2px;
    }

    .town-item-price {
      font-size: clamp(0.68rem, 1.1vw, 0.8rem);
      color: #d4b870;
      font-weight: 600;
      white-space: nowrap;
      text-align: right;
    }

    .town-item-buy-btn {
      padding: 6px 14px;
      background: rgba(42, 32, 24, 0.9);
      border: 1px solid rgba(200, 160, 32, 0.3);
      border-radius: 4px;
      color: #c8d0a0;
      font-family: inherit;
      font-size: 0.65rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      cursor: pointer;
      min-height: 44px;
      min-width: 54px;
      transition: background 0.12s, border-color 0.12s, color 0.12s;
      white-space: nowrap;
    }

    .town-item-buy-btn:hover,
    .town-item-buy-btn:focus-visible {
      background: rgba(200, 160, 32, 0.12);
      border-color: rgba(200, 160, 32, 0.6);
      color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    .town-item-buy-btn:disabled {
      opacity: 0.38;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Haggle bar (shop panels) ────────────────────────────────── */
    .town-haggle-bar {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    .town-haggle-badge {
      font-size: 0.6rem;
      padding: 2px 7px;
      border-radius: 3px;
      background: rgba(64, 160, 96, 0.18);
      border: 1px solid rgba(64, 160, 96, 0.4);
      color: #40a060;
    }

    /* ── Message bar inside content ─────────────────────────────── */
    .town-message {
      font-size: clamp(0.62rem, 1vw, 0.74rem);
      color: #a09070;
      min-height: 28px;
      display: flex;
      align-items: center;
      padding: 6px 0 0;
    }
    .town-message.success { color: #40a060; }
    .town-message.error   { color: #c83020; }
    .town-message.warn    { color: #e07820; }

    /* ── Inn panel ────────────────────────────────────────────────── */
    .town-inn-card {
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(200, 160, 32, 0.12);
      border-radius: 6px;
      padding: 20px 24px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      max-width: 480px;
    }

    .town-inn-desc {
      font-size: clamp(0.7rem, 1.1vw, 0.82rem);
      color: #a09070;
      line-height: 1.6;
    }

    .town-inn-cost {
      font-size: clamp(0.75rem, 1.2vw, 0.9rem);
      color: #d4b870;
      font-weight: 600;
    }

    /* ── Hireable NPCs (Tavern) ──────────────────────────────────── */
    .town-npc-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .town-npc-row {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(200, 160, 32, 0.1);
      border-radius: 4px;
      min-height: 60px;
      transition: background 0.12s, border-color 0.12s;
    }

    .town-npc-row:hover {
      background: rgba(200, 160, 32, 0.06);
      border-color: rgba(200, 160, 32, 0.22);
    }

    .town-npc-avatar {
      font-size: 1.6rem;
      width: 36px;
      text-align: center;
    }

    .town-npc-info { min-width: 0; }

    .town-npc-name {
      font-size: clamp(0.72rem, 1.15vw, 0.85rem);
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .town-npc-meta {
      font-size: 0.62rem;
      color: #706050;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-top: 2px;
    }

    .town-npc-wage {
      font-size: clamp(0.65rem, 1vw, 0.78rem);
      color: #d4b870;
      font-weight: 600;
      white-space: nowrap;
      text-align: right;
      margin-bottom: 6px;
    }

    .town-npc-actions {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }

    /* ── Blacksmith panel ────────────────────────────────────────── */
    .town-smith-card {
      background: rgba(255, 255, 255, 0.025);
      border: 1px solid rgba(200, 160, 32, 0.12);
      border-radius: 6px;
      padding: 20px 24px;
      max-width: 520px;
    }

    .town-repair-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 12px;
    }

    .town-repair-row {
      display: grid;
      grid-template-columns: auto 1fr auto auto;
      align-items: center;
      gap: 10px;
      padding: 7px 10px;
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(200, 160, 32, 0.08);
      border-radius: 4px;
      min-height: 48px;
    }

    .town-repair-condition {
      font-size: 0.6rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .town-repair-condition.good     { color: #40a060; }
    .town-repair-condition.worn     { color: #e07820; }
    .town-repair-condition.damaged  { color: #c83020; }

    /* ── Generic town btn ─────────────────────────────────────────── */
    .town-btn {
      padding: 9px 20px;
      background: rgba(42, 32, 24, 0.9);
      border: 1px solid rgba(200, 160, 32, 0.3);
      border-radius: 4px;
      color: #c8d0a0;
      font-family: inherit;
      font-size: clamp(0.68rem, 1.1vw, 0.8rem);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      cursor: pointer;
      min-height: 44px;
      min-width: 80px;
      transition: background 0.12s, border-color 0.12s, color 0.12s;
    }

    .town-btn:hover,
    .town-btn:focus-visible {
      background: rgba(200, 160, 32, 0.12);
      border-color: rgba(200, 160, 32, 0.6);
      color: #c8a020;
      outline: 2px solid #e8c040;
      outline-offset: 2px;
    }

    .town-btn:disabled {
      opacity: 0.38;
      cursor: not-allowed;
      pointer-events: none;
    }

    .town-btn.primary {
      background: #4a3a10;
      border-color: #c8a020;
      color: #c8a020;
    }
    .town-btn.primary:hover {
      background: #6a5a20;
    }

    .town-btn.danger {
      border-color: rgba(200, 48, 32, 0.5);
      color: #c83020;
    }
    .town-btn.danger:hover {
      background: rgba(200, 48, 32, 0.1);
      border-color: #c83020;
    }

    /* ── Footer bar ───────────────────────────────────────────────── */
    .town-footer {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: clamp(8px, 1.5vh, 12px) clamp(12px, 2vw, 24px);
      background: rgba(0, 0, 0, 0.4);
      border-top: 1px solid rgba(200, 160, 32, 0.15);
      flex-shrink: 0;
      gap: 12px;
    }

    /* ── Empty state ──────────────────────────────────────────────── */
    .town-empty {
      color: #706050;
      font-size: clamp(0.68rem, 1.1vw, 0.8rem);
      padding: 12px 0;
      font-style: italic;
    }

    /* ══════════════════════════════════════════════════════════════
       Responsive — Mobile portrait: sidebar becomes tab row
       ══════════════════════════════════════════════════════════════ */
    @media (max-width: 767px) and (orientation: portrait) {
      .town-body {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
      }

      .town-sidebar {
        flex-direction: row;
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        border-right: none;
        border-bottom: 1px solid rgba(200, 160, 32, 0.15);
        padding: 8px;
        gap: 6px;
        scrollbar-width: thin;
        max-height: none;
      }

      .town-sidebar-heading {
        display: none;
      }

      .town-service-btn {
        flex-direction: column;
        gap: 4px;
        min-width: 70px;
        padding: 8px 10px;
        font-size: 0.58rem;
        text-align: center;
      }

      .town-service-icon {
        font-size: 1.2rem;
        width: auto;
      }

      .town-item-row {
        grid-template-columns: auto 1fr auto;
      }

      .town-item-price {
        display: none;
      }

      .town-npc-row {
        grid-template-columns: auto 1fr auto;
      }

      .town-repair-row {
        grid-template-columns: auto 1fr auto;
      }
    }

    /* Mobile landscape: narrower sidebar */
    @media (max-width: 1023px) and (orientation: landscape) {
      .town-body {
        grid-template-columns: 160px 1fr;
      }

      .town-service-btn {
        font-size: 0.65rem;
        padding: 7px 10px;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Race icons ───────────────────────────────────────────────────────────────

const RACE_ICONS = {
  norse:    '\u2694',
  dwarf:    '\u26CF',
  elf:      '\u{1F9DD}',
  orc:      '\uD83D\uDC79',
  human:    '\uD83D\uDC64',
  default:  '\uD83C\uDFD8',
};

const TOWN_TYPE_LABELS = {
  village:  'Village',
  town:     'Town',
  city:     'City',
  fort:     'Fortress',
  hamlet:   'Hamlet',
  default:  'Settlement',
};

// ─── Fallback data (used when TownSystem is not yet available) ────────────────

const FALLBACK_SHOP_WEAPONS = [
  { id: 'iron_sword',   name: 'Iron Sword',      type: 'Weapon', subtype: 'Sword',   price: 45,  icon: '\u2694' },
  { id: 'hand_axe',     name: 'Hand Axe',         type: 'Weapon', subtype: 'Axe',    price: 32,  icon: '\u2694' },
  { id: 'hunting_bow',  name: 'Hunting Bow',       type: 'Weapon', subtype: 'Bow',   price: 60,  icon: '\uD83C\uDFF9' },
  { id: 'iron_spear',   name: 'Iron Spear',        type: 'Weapon', subtype: 'Polearm', price: 38, icon: '\u2694' },
];

const FALLBACK_SHOP_ARMOR = [
  { id: 'leather_vest',    name: 'Leather Vest',    type: 'Armor', subtype: 'Light',  price: 30,  icon: '\uD83E\uDDE5' },
  { id: 'chain_shirt',     name: 'Chain Shirt',      type: 'Armor', subtype: 'Medium', price: 80,  icon: '\uD83E\uDDE5' },
  { id: 'iron_shield',     name: 'Iron Shield',      type: 'Shield', subtype: 'Shield', price: 40, icon: '\uD83D\uDEE1' },
  { id: 'iron_helm',       name: 'Iron Helm',        type: 'Armor', subtype: 'Helmet', price: 35, icon: '\uD83E\uDDE2' },
];

const FALLBACK_HIREABLE_NPCS = [
  { id: 'npc_sigrid',   name: 'Sigrid Ironbark',  cls: 'Warrior',  level: 2, wage: 8,  avatar: '\u2694' },
  { id: 'npc_bjorn',    name: 'Bjorn Halfhand',   cls: 'Scout',    level: 1, wage: 5,  avatar: '\uD83C\uDFF9' },
  { id: 'npc_thyra',    name: 'Thyra Ashveil',    cls: 'Runecaster', level: 3, wage: 14, avatar: '\u{1F52E}' },
  { id: 'npc_gunnar',   name: 'Gunnar Stoutaxe',  cls: 'Berserker', level: 2, wage: 9,  avatar: '\uD83D\uDD25' },
  { id: 'npc_ingrid',   name: 'Ingrid Wolfborn',  cls: 'Shaman',    level: 2, wage: 11, avatar: '\uD83D\uDC3A' },
];

const FALLBACK_REPAIR_ITEMS = [
  { id: 'iron_sword_worn',   name: 'Iron Sword',    condition: 'worn',    cost: 12 },
  { id: 'leather_vest_dmg',  name: 'Leather Vest',  condition: 'damaged', cost: 8  },
  { id: 'iron_shield_worn',  name: 'Iron Shield',   condition: 'worn',    cost: 10 },
];

// ─── Service definitions ──────────────────────────────────────────────────────

/**
 * Returns the list of service definitions for a given town.
 * Each service has: id, label, icon, available(town)
 * @param {object} town
 * @returns {Array<{id:string, label:string, icon:string}>}
 */
function getAvailableServices(town) {
  const type = town?.type ?? 'village';
  const all = [
    { id: 'weapons', label: 'Weapon Shop', icon: '\u2694' },
    { id: 'armor',   label: 'Armor Shop',  icon: '\uD83E\uDDE5' },
    { id: 'inn',     label: 'Inn',         icon: '\uD83C\uDFE0' },
    { id: 'tavern',  label: 'Tavern',      icon: '\uD83C\uDF7A' },
    { id: 'smith',   label: 'Blacksmith',  icon: '\uD83D\uDD28' },
  ];

  // Smaller settlements may lack some services
  const limited = { hamlet: ['inn', 'weapons'], village: ['weapons', 'armor', 'inn', 'tavern'] };
  const allowed = limited[type] ?? null;

  return allowed ? all.filter(s => allowed.includes(s.id)) : all;
}

// ─── TownPanel ────────────────────────────────────────────────────────────────

class TownPanel extends UIPanel {
  constructor() {
    injectStyles();
    super('panel-town', { trapFocus: false, zLayer: 'modal' });

    /** @type {object|null} Current town data */
    this._town = null;

    /** @type {string} Active service id */
    this._activeService = 'weapons';

    /** @type {boolean} Whether haggle has been used this visit (per shop) */
    this._haggledServices = new Set();

    /** @type {number} Current discount multiplier (1.0 = none) */
    this._discountMultiplier = 1.0;

    /** @type {Function|null} Unsub for EventBus listener */
    this._unsub = null;
  }

  // ─── UIPanel lifecycle ───────────────────────────────────────────────────

  /**
   * Show the town panel.
   * @param {object|null} town — Town data object from WorldState or debug call
   */
  show(town = null) {
    this._town = town ?? this._getFallbackTown();
    this._activeService = getAvailableServices(this._town)[0]?.id ?? 'weapons';
    this._haggledServices = new Set();
    this._discountMultiplier = 1.0;

    this._render();
    super.show(this._town);
    this._bindEvents();

    DebugLog.info(`[world] TownPanel opened; town=${this._town.name} type=${this._town.type}`);
  }

  hide() {
    super.hide();
    EventBus.emit('town:exit', { town: this._town });
    DebugLog.info('[world] TownPanel closed');
  }

  destroy() {
    if (this._unsub) { this._unsub(); this._unsub = null; }
    super.destroy();
  }

  // ─── DOM build ───────────────────────────────────────────────────────────

  _buildHTML() {
    return `
      <div class="town-header">
        <div class="town-header-left">
          <div class="town-title" id="town-name-display">Town</div>
          <div class="town-subtitle">
            <span class="town-race-icon" id="town-race-icon"></span>
            <span id="town-type-badge" class="town-type-badge">Settlement</span>
          </div>
        </div>
        <button class="town-close" id="town-close-btn" aria-label="Leave town" tabindex="0">&times;</button>
      </div>
      <div class="town-body">
        <nav class="town-sidebar" id="town-sidebar" role="navigation" aria-label="Town services">
          <div class="town-sidebar-heading">Services</div>
          <div id="town-service-list"></div>
        </nav>
        <div class="town-content">
          <div class="town-content-inner" id="town-content-area" role="region" aria-live="polite"></div>
        </div>
      </div>
      <div class="town-footer">
        <button class="town-btn danger" id="town-leave-btn" tabindex="0">Leave Town</button>
      </div>
    `;
  }

  // ─── Rendering ───────────────────────────────────────────────────────────

  _render() {
    this._renderHeader();
    this._renderSidebar();
    this._renderActiveService();
  }

  _renderHeader() {
    const town = this._town;
    const nameEl   = this.el.querySelector('#town-name-display');
    const iconEl   = this.el.querySelector('#town-race-icon');
    const badgeEl  = this.el.querySelector('#town-type-badge');

    if (nameEl)  nameEl.textContent  = town.name ?? 'Unknown Town';
    if (iconEl)  iconEl.textContent  = RACE_ICONS[town.race] ?? RACE_ICONS.default;
    if (badgeEl) badgeEl.textContent = TOWN_TYPE_LABELS[town.type] ?? TOWN_TYPE_LABELS.default;
  }

  _renderSidebar() {
    const list = this.el.querySelector('#town-service-list');
    if (!list) return;
    list.innerHTML = '';

    const services = getAvailableServices(this._town);
    for (const svc of services) {
      const btn = document.createElement('button');
      btn.className = 'town-service-btn' + (svc.id === this._activeService ? ' active' : '');
      btn.dataset.service = svc.id;
      btn.tabIndex = 0;
      btn.setAttribute('aria-current', svc.id === this._activeService ? 'true' : 'false');
      btn.innerHTML = `
        <span class="town-service-icon">${svc.icon}</span>
        <span>${svc.label}</span>
      `;
      btn.addEventListener('click', () => this._selectService(svc.id));
      list.appendChild(btn);
    }
  }

  _renderActiveService() {
    const area = this.el.querySelector('#town-content-area');
    if (!area) return;

    switch (this._activeService) {
      case 'weapons': this._renderShopPanel(area, 'weapons'); break;
      case 'armor':   this._renderShopPanel(area, 'armor');   break;
      case 'inn':     this._renderInnPanel(area);     break;
      case 'tavern':  this._renderTavernPanel(area);  break;
      case 'smith':   this._renderSmithPanel(area);   break;
      default:
        area.innerHTML = `<div class="town-empty">This service is not yet available.</div>`;
    }
  }

  // ─── Shop panel ──────────────────────────────────────────────────────────

  _renderShopPanel(area, shopType) {
    const items = this._getShopItems(shopType);
    const gold  = WorldState.partyGold ?? 0;
    const hasHaggled  = this._haggledServices.has(shopType);
    const discountPct = hasHaggled ? Math.round((1 - this._discountMultiplier) * 100) : 0;

    const label = shopType === 'weapons' ? 'Weapon Shop' : 'Armor Shop';

    area.innerHTML = `
      <div class="town-content-title">${label}</div>
      <div class="town-gold-bar">
        <span>Party Gold:</span>
        <span id="town-gold-val">${gold}g</span>
      </div>
      <div class="town-haggle-bar">
        <button class="town-btn" id="town-haggle-btn" ${hasHaggled ? 'disabled' : ''}>
          Haggle
        </button>
        ${hasHaggled && discountPct > 0
          ? `<span class="town-haggle-badge">-${discountPct}% discount</span>`
          : ''}
      </div>
      <div class="town-item-list" id="town-shop-list"></div>
      <div class="town-message" id="town-shop-msg"></div>
    `;

    const list = area.querySelector('#town-shop-list');
    if (items.length === 0) {
      list.innerHTML = '<div class="town-empty">No items in stock.</div>';
    } else {
      for (const item of items) {
        const price = Math.max(1, Math.floor(item.price * this._discountMultiplier));
        const canAfford = gold >= price;

        const row = document.createElement('div');
        row.className = 'town-item-row';
        row.innerHTML = `
          <span class="town-item-icon">${item.icon ?? '\uD83D\uDCE6'}</span>
          <div class="town-item-info">
            <div class="town-item-name">${item.name}</div>
            <div class="town-item-sub">${item.type}${item.subtype ? ' \u00B7 ' + item.subtype : ''}</div>
          </div>
          <span class="town-item-price">${price}g</span>
          <button class="town-item-buy-btn"
            data-item-id="${item.id}"
            data-price="${price}"
            ${canAfford ? '' : 'disabled'}
            aria-label="Buy ${item.name} for ${price} gold">
            Buy
          </button>
        `;
        list.appendChild(row);
      }
    }

    // Bind buy buttons
    area.querySelectorAll('.town-item-buy-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemId   = btn.dataset.itemId;
        const price    = parseInt(btn.dataset.price, 10);
        this._buyItem(itemId, price, shopType, items);
      });
    });

    // Bind haggle
    const haggleBtn = area.querySelector('#town-haggle-btn');
    if (haggleBtn) {
      haggleBtn.addEventListener('click', () => this._doHaggle(shopType));
    }
  }

  // ─── Inn panel ───────────────────────────────────────────────────────────

  _renderInnPanel(area) {
    const restCost = this._getInnRestCost();
    const gold     = WorldState.partyGold ?? 0;
    const canAfford = gold >= restCost;

    area.innerHTML = `
      <div class="town-content-title">Inn</div>
      <div class="town-inn-card">
        <p class="town-inn-desc">
          The innkeeper offers warm beds and hot meals. The party can rest here
          to fully restore health, clear fatigue, and prepare for the road ahead.
        </p>
        <div class="town-inn-cost">Cost: ${restCost}g per rest</div>
        <div class="town-gold-bar" style="justify-content:flex-start; margin:0">
          Party Gold: <span id="town-inn-gold">${gold}g</span>
        </div>
        <button class="town-btn primary" id="town-rest-btn"
          ${canAfford ? '' : 'disabled'}
          aria-label="Rest at the inn for ${restCost} gold">
          Rest (Heal Party)
        </button>
        <div class="town-message" id="town-inn-msg"></div>
      </div>
    `;

    const restBtn = area.querySelector('#town-rest-btn');
    if (restBtn) {
      restBtn.addEventListener('click', () => this._doRest(restCost));
    }
  }

  // ─── Tavern / hire panel ──────────────────────────────────────────────────

  _renderTavernPanel(area) {
    const npcs = this._getHireableNPCs();

    area.innerHTML = `
      <div class="town-content-title">Tavern</div>
      <p style="font-size:clamp(0.68rem,1.1vw,0.8rem);color:#a09070;margin-bottom:14px;line-height:1.5">
        Mercenaries and wanderers gather here, seeking work. Hiring a companion
        costs their daily wage from the party treasury each in-game day.
      </p>
      <div class="town-npc-list" id="town-npc-list"></div>
      <div class="town-message" id="town-tavern-msg"></div>
    `;

    const list = area.querySelector('#town-npc-list');
    if (npcs.length === 0) {
      list.innerHTML = '<div class="town-empty">No mercenaries available at this time.</div>';
      return;
    }

    for (const npc of npcs) {
      const row = document.createElement('div');
      row.className = 'town-npc-row';
      row.innerHTML = `
        <div class="town-npc-avatar">${npc.avatar ?? '\uD83D\uDC64'}</div>
        <div class="town-npc-info">
          <div class="town-npc-name">${npc.name}</div>
          <div class="town-npc-meta">${npc.cls} \u00B7 Level ${npc.level}</div>
        </div>
        <div class="town-npc-actions">
          <div class="town-npc-wage">${npc.wage}g/day</div>
          <button class="town-btn" style="min-width:70px;font-size:0.65rem"
            data-npc-id="${npc.id}"
            aria-label="Hire ${npc.name}">
            Hire
          </button>
        </div>
      `;
      list.appendChild(row);
    }

    // Bind hire buttons
    area.querySelectorAll('[data-npc-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const npcId = btn.dataset.npcId;
        const npc   = npcs.find(n => n.id === npcId);
        if (npc) this._doHireNPC(npc);
      });
    });
  }

  // ─── Blacksmith panel ─────────────────────────────────────────────────────

  _renderSmithPanel(area) {
    const repairItems  = this._getRepairableItems();
    const repairAllCost = repairItems.reduce((sum, i) => sum + (i.cost ?? 0), 0);
    const gold = WorldState.partyGold ?? 0;
    const canRepairAll = gold >= repairAllCost && repairItems.length > 0;

    area.innerHTML = `
      <div class="town-content-title">Blacksmith</div>
      <div class="town-smith-card">
        <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:10px">
          <span style="font-size:clamp(0.7rem,1.1vw,0.82rem);color:#a09070">
            Repair cost: <strong style="color:#d4b870">${repairAllCost}g</strong>
          </span>
          <button class="town-btn primary" id="town-repair-all-btn"
            ${canRepairAll ? '' : 'disabled'}
            aria-label="Repair all damaged equipment for ${repairAllCost} gold">
            Repair All
          </button>
        </div>
        <div class="town-repair-list" id="town-repair-list"></div>
        <div class="town-message" id="town-smith-msg"></div>
      </div>
    `;

    const repairList = area.querySelector('#town-repair-list');
    if (repairItems.length === 0) {
      repairList.innerHTML = '<div class="town-empty">All equipment is in good condition.</div>';
    } else {
      for (const item of repairItems) {
        const row = document.createElement('div');
        row.className = 'town-repair-row';
        row.innerHTML = `
          <span class="town-item-icon">\u2694</span>
          <div class="town-item-info">
            <div class="town-item-name">${item.name}</div>
            <div class="town-item-sub">
              <span class="town-repair-condition ${item.condition}">${item.condition}</span>
            </div>
          </div>
          <span class="town-item-price">${item.cost}g</span>
          <button class="town-btn" style="font-size:0.62rem;min-width:70px"
            data-repair-id="${item.id}"
            ${gold >= item.cost ? '' : 'disabled'}
            aria-label="Repair ${item.name} for ${item.cost} gold">
            Repair
          </button>
        `;
        repairList.appendChild(row);
      }
    }

    // Repair all
    const repairAllBtn = area.querySelector('#town-repair-all-btn');
    if (repairAllBtn) {
      repairAllBtn.addEventListener('click', () => this._doRepairAll(repairItems, repairAllCost));
    }

    // Individual repair
    area.querySelectorAll('[data-repair-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id   = btn.dataset.repairId;
        const item = repairItems.find(i => i.id === id);
        if (item) this._doRepairItem(item);
      });
    });
  }

  // ─── Service selection ────────────────────────────────────────────────────

  _selectService(serviceId) {
    if (serviceId === this._activeService) return;
    this._activeService = serviceId;

    // Update sidebar active state
    this.el.querySelectorAll('.town-service-btn').forEach(btn => {
      const isActive = btn.dataset.service === serviceId;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this._renderActiveService();
    this._refreshFocusables();

    DebugLog.info(`[world] TownPanel service selected: ${serviceId}`);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  _buyItem(itemId, price, shopType, items) {
    const gold = WorldState.partyGold ?? 0;
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    if (gold < price) {
      this._setServiceMessage(shopType, 'Not enough gold.', 'error');
      return;
    }

    // Attempt TownSystem if available
    let purchased = false;
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.buyItem) {
        TownSystem.buyItem(itemId, price);
        purchased = true;
      }
    } catch (_) { /* TownSystem not available */ }

    if (!purchased) {
      // Fallback: deduct gold directly
      WorldState.partyGold = gold - price;
    }

    DebugLog.event(`[world] TownPanel buy; item=${itemId} price=${price} gold=${WorldState.partyGold}`);
    this._setServiceMessage(shopType, `Purchased ${item.name} for ${price}g.`, 'success');
    this._renderActiveService();
  }

  _doRest(cost) {
    const gold = WorldState.partyGold ?? 0;
    if (gold < cost) {
      this._setServiceMessage('inn', 'Not enough gold to rest.', 'error');
      return;
    }

    let result = null;
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.restAtInn) {
        result = TownSystem.restAtInn([]);
      }
    } catch (_) { /* fallback */ }

    WorldState.partyGold = gold - cost;

    const msg = result?.message ?? 'The party rests and recovers fully. (HP restored)';
    DebugLog.event(`[world] TownPanel rest; cost=${cost} gold=${WorldState.partyGold}`);
    this._setServiceMessage('inn', msg, 'success');
    this._renderActiveService();
  }

  _doHireNPC(npc) {
    let result = null;
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.hireNPC) {
        result = TownSystem.hireNPC(npc, []);
      }
    } catch (_) { /* fallback */ }

    const msg = result?.message ?? `${npc.name} has joined the party! (${npc.wage}g/day)`;
    const type = result?.success === false ? 'error' : 'success';
    DebugLog.event(`[world] TownPanel hire; npc=${npc.id} wage=${npc.wage}`);
    this._setServiceMessage('tavern', msg, type);
    EventBus.emit('party:npc_hired', { npc });
  }

  _doRepairItem(item) {
    const gold = WorldState.partyGold ?? 0;
    if (gold < item.cost) {
      this._setServiceMessage('smith', 'Not enough gold.', 'error');
      return;
    }

    let result = null;
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.repairItem) {
        result = TownSystem.repairItem(null, item.id);
      }
    } catch (_) { /* fallback */ }

    WorldState.partyGold = gold - item.cost;
    const msg = result?.message ?? `${item.name} has been repaired.`;
    DebugLog.event(`[world] TownPanel repair; item=${item.id} cost=${item.cost}`);
    this._setServiceMessage('smith', msg, 'success');
    this._renderActiveService();
  }

  _doRepairAll(items, totalCost) {
    const gold = WorldState.partyGold ?? 0;
    if (gold < totalCost) {
      this._setServiceMessage('smith', 'Not enough gold to repair everything.', 'error');
      return;
    }

    WorldState.partyGold = gold - totalCost;
    DebugLog.event(`[world] TownPanel repairAll; cost=${totalCost} gold=${WorldState.partyGold}`);
    this._setServiceMessage('smith', `All equipment repaired for ${totalCost}g.`, 'success');
    this._renderActiveService();
  }

  _doHaggle(shopType) {
    if (this._haggledServices.has(shopType)) {
      this._setServiceMessage(shopType, 'You have already haggled here today.', 'warn');
      return;
    }

    let result = null;
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.haggle) {
        result = TownSystem.haggle(shopType, this._town);
      }
    } catch (_) { /* fallback */ }

    // Fallback: d20 check
    if (!result) {
      const roll = Math.floor(Math.random() * 20) + 1;
      const dc   = 10 + (this._town?.regionLevel ?? 1);
      if (roll >= dc) {
        const discount = 0.10 + Math.random() * 0.15;
        result = { success: true, discount, message: `Haggle success (rolled ${roll} vs DC ${dc}) — ${Math.round(discount * 100)}% off!` };
      } else {
        result = { success: false, discount: 0, message: `Haggle failed (rolled ${roll} vs DC ${dc}).` };
      }
    }

    this._haggledServices.add(shopType);

    if (result.success && result.discount > 0) {
      this._discountMultiplier = 1.0 - result.discount;
    }

    const msgType = result.success ? 'success' : 'error';
    DebugLog.event(`[world] TownPanel haggle; shop=${shopType} success=${result.success} discount=${result.discount ?? 0}`);
    this._setServiceMessage(shopType, result.message, msgType);
    this._renderActiveService();
  }

  // ─── Data helpers ─────────────────────────────────────────────────────────

  _getShopItems(shopType) {
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.getShopInventory) {
        const items = TownSystem.getShopInventory(shopType, this._town, this._town?.regionLevel ?? 1, Math.random);
        if (Array.isArray(items) && items.length > 0) return items;
      }
    } catch (_) { /* fall through */ }

    return shopType === 'weapons' ? FALLBACK_SHOP_WEAPONS : FALLBACK_SHOP_ARMOR;
  }

  _getInnRestCost() {
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.getRestCost) return TownSystem.getRestCost(this._town);
    } catch (_) { /* fall through */ }

    const level = this._town?.regionLevel ?? 1;
    return 5 + level * 3;
  }

  _getHireableNPCs() {
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.getHireableNPCs) {
        const npcs = TownSystem.getHireableNPCs(this._town);
        if (Array.isArray(npcs) && npcs.length > 0) return npcs;
      }
    } catch (_) { /* fall through */ }

    return FALLBACK_HIREABLE_NPCS;
  }

  _getRepairableItems() {
    try {
      const TownSystem = this._getTownSystem();
      if (TownSystem?.getRepairableItems) {
        const items = TownSystem.getRepairableItems([]);
        if (Array.isArray(items) && items.length > 0) return items;
      }
    } catch (_) { /* fall through */ }

    return FALLBACK_REPAIR_ITEMS;
  }

  /** Safely retrieve TownSystem without throwing if absent. */
  _getTownSystem() {
    if (typeof window !== 'undefined' && window.__townSystem) return window.__townSystem;
    // Try dynamic module cache (Vite keeps modules in memory after first import)
    try {
      // eslint-disable-next-line no-undef
      return globalThis.__townSystem ?? null;
    } catch (_) {
      return null;
    }
  }

  _getFallbackTown() {
    return {
      id:          'fallback_town',
      name:        'Ironhaven',
      type:        'town',
      race:        'norse',
      regionLevel: 1,
    };
  }

  // ─── Message helpers ──────────────────────────────────────────────────────

  /**
   * Set a message inside the currently active service panel.
   * @param {string} service
   * @param {string} text
   * @param {string} [type=''] — 'success'|'error'|'warn'|''
   */
  _setServiceMessage(service, text, type = '') {
    // Map service name to message element selector
    const msgIds = {
      weapons: '#town-shop-msg',
      armor:   '#town-shop-msg',
      inn:     '#town-inn-msg',
      tavern:  '#town-tavern-msg',
      smith:   '#town-smith-msg',
    };

    const sel = msgIds[service];
    if (!sel) return;

    const el = this.el.querySelector(sel);
    if (!el) return;

    el.textContent = text;
    el.className = 'town-message' + (type ? ' ' + type : '');
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  _bindEvents() {
    // Close button
    const closeBtn = this.el.querySelector('#town-close-btn');
    if (closeBtn && !closeBtn._townBound) {
      closeBtn.addEventListener('click', () => this.hide());
      closeBtn._townBound = true;
    }

    // Leave town button (footer)
    const leaveBtn = this.el.querySelector('#town-leave-btn');
    if (leaveBtn && !leaveBtn._townBound) {
      leaveBtn.addEventListener('click', () => this.hide());
      leaveBtn._townBound = true;
    }
  }

  // ─── Public init (singleton pattern like InventoryManager) ───────────────

  /**
   * Initialize the panel and register EventBus listeners.
   * Call once from main.js.
   */
  init() {
    // Listen for town:enter to auto-open
    this._unsub = EventBus.on('town:enter', ({ town } = {}) => {
      this.show(town ?? null);
    });

    DebugLog.info('[world] TownPanel initialized');
  }
}

// ─── Singleton export ─────────────────────────────────────────────────────────

const townPanel = new TownPanel();
export default townPanel;
